/**
 * 416 the commander's ambush (DECISIONS 416, the owner: "a bot that's got some new legible and interesting skills"; behind
 * `GameConfig.commanderAmbush`, on unless false — DECISIONS 433 — one value or per team). A squad the commander keeps on a point its side owns
 * (a hold — 66.13's guard, the threatened rule's holder) lies in wait on the enemy's way to the point instead of on it: on
 * the line from where the side's picture last held enemies (else the enemy's base) to the point — the way the bots walk on
 * foot — 30–60 people out, at a spot where a crouched body (as its members lie) is hidden from eyes coming along that line,
 * by the ground or the props, while a standing eye sees along it, with room for two. There it holds as 66.11's ambush
 * (hidden, fire held, every member up on the same tick when an enemy walks into the zone, a member is hit or an enemy is
 * close), fights, and once the zone is clear goes back to its point. Nothing authored: the site is read live each time.
 *
 * Legible: the squad's task carries `skill: { name: 'ambush', phase, since }` for the card and lane E's callouts —
 * `moving` (to the site) → `waiting` (down, fire held) → `sprung` (the volley) → `holding` (fighting at the site till it is
 * clear); the leader's step's `ambush.armed` / `sprung` (66.11) are the "Hold fire" and "Open fire!" cues.
 *
 * Measured first (sweeps: the site-pick number, fh-1…6, 900 s): a hidden site on the line for 79 % of guard reads; where an
 * enemy came to the point in the next 90 s, one passed through the zone first 71 % of the time (59 % with 5 s or more to
 * spare); 56 % of all arrivals came through it.
 *
 * Three hooks in the commander (ai/squadOrders.ts): `plan` in the planner list after `plays` picks the sites at the re-plan;
 * `step` once a second moves the phases on and renews the tasks; `holds` keeps a lying squad's hold through the re-plan (as
 * the standoff manoeuvre's squads keep theirs). The task is the routine's rank (6, claim `ambush`), so any
 * planner after it, a play, an objective or a human takes the squad back. Pure in the state, with the planner's own memory.
 */
import type { GameState, SquadOrder, Stance, Vec3 } from '../../types';
import type { SkillTag, TaskClaim } from '../../types';
import { AMBUSH } from '../../constants/action';
import { PHYS_DT } from '../../constants';
import { personSetOf, PERSON_BODY_HEIGHT } from '../../core/scaleProfile';
import { teamPicture } from '../../core/picture';
import { personPointOn } from '../../core/fineTerrain';
import { brainFace } from '../../core/brainFace';
import { vecNormalize } from '../../math';
import { arc, besides, hiddenSpot, slerp, takenBy } from './spots';

export const AMBUSH_PLAN = {
  /** The site: tried this far out from the point along the enemy's way, nearest first (people). */
  distancesPeople: [30, 35, 40, 45, 50, 60],
  /**
   * … and these when the picture's nearest enemy is within `nearPeople` of the point: the walk out is the risk (at 25 a side,
   * 6 of 18 ambushes lost two of three walking to a site 30–60 people out), so a shorter one while the enemy is near.
   */
  nearPeople: 100,
  nearDistancesPeople: [20, 25, 30],
  /** … within this of the way's point there (people): the way passes inside the zone (AMBUSH.zonePeople). */
  spotPeople: 12,
  /**
   * The eyes it hides from (the fire it expects): on the way, each of these farther out than the site, and at the first, as
   * far to either side of the way (people) — an enemy coming does not keep to the line. (A second eye at 50 left 5 of
   * Foothold's 120 point-and-side pairs a site; at 25 alone, 12.)
   */
  eyeBackPeople: [25] as number[],
  eyeSidePeople: 15,
  /** The stance the site hides from every eye (66.11's members lie crouched unless their post is a lip's: crouch hides them as they lie). */
  hideStance: 'crouch' as Stance,
  /** Room for the squad: this many fitting spots (the pick among them) within `roomPeople` of the pick — its members lie at posts round it. */
  roomSpots: 2,
  roomPeople: 6,
  /** The picture's enemies the way is read from: this far from the point (people) — nearer is a fight at the point, farther is not coming yet. */
  sourceMinPeople: 40,
  sourceMaxPeople: 400,
  /** Laid only by a squad with this many on foot (a squad down to two is one hit from `few` — at 25 a side, 8 of 9 such ends were deaths). */
  pickOnFoot: 3,
  /** No ambush while an enemy the side knows of stands this close to the point, or to the site (people): the guard stays and fights, and never walks out into a fight to lie down in it. */
  contactPeople: 30,
  /** The same site: within this of it (people) — a leader's step there, a site read again that hardly moved. */
  arrivePeople: 5,
  /** The volley's phase lasts this long before `holding` (s). */
  sprungSeconds: 5,
  /** Holding ends when the side knows no enemy within this many zones of the site for `clearSeconds`, or after `holdMaxSeconds`. */
  clearZones: 2,
  clearSeconds: 15,
  holdMaxSeconds: 90,
  /** Waiting this long unsprung, the site is read again (the picture moves). */
  repickSeconds: 120,
  /** After an ambush ends, none for this squad for this long (s): it goes back to its point first. */
  cooldownSeconds: 30,
  /** A task runs this long unless renewed (s); `step` renews it every `renewSeconds`. */
  taskSeconds: 15,
  renewSeconds: 10,
};

/** Is the commander's ambush on for `team` (one value or its side's; on unless false, DECISIONS 433)? It needs 66.11's ambush hold, which needs coverMoves. */
export function ambushOn(state: Pick<GameState, 'config'>, team: number): boolean {
  const v = state.config.commanderAmbush;
  const cover = state.config.coverMoves;   // (66.19: on unless false, per team as actionBot reads it)
  return (Array.isArray(v) ? v[team] !== false : v !== false) && (Array.isArray(cover) ? cover[team] !== false : cover !== false);
}

/**
 * Where a guard of `point` lies in wait against `team`'s enemies: the way from the side's nearest believed enemy within the
 * source band of the point (else the enemy's base) to the point, and the nearest spot to it, at the first distance out that
 * has one, where a crouched body (the stance the members lie at: 66.11's lying stance is crouched unless their post is a
 * lip's) is hidden — by the ground or by the props — from every eye on that way, and a standing eye sees along it; with room
 * for the squad (`roomSpots` such spots near it). Null when nothing is coming from anywhere or no spot hides. Pure.
 */
export function ambushSite(state: GameState, team: number, planetIndex: number, point: Vec3, tick: number): { dir: Vec3; people: number } | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, r = planet.radius;
  let src: Vec3 | null = null, best = Infinity;
  for (const b of teamPicture(state, team, tick)) {
    if (b.planetIndex !== planetIndex) continue;
    const d = arc(b.surfaceDir, point) * r;
    if (d >= AMBUSH_PLAN.sourceMinPeople * person && d <= AMBUSH_PLAN.sourceMaxPeople * person && d < best) { best = d; src = b.surfaceDir; }
  }
  const close = src !== null && best <= AMBUSH_PLAN.nearPeople * person;   // (the picture's enemy near: the shorter walk)
  src ??= state.board?.bases?.find(b => b.team !== team && b.planetIndex === planetIndex)?.dir ?? null;
  if (!src) return null;
  const way = arc(point, src);
  for (const people of close ? AMBUSH_PLAN.nearDistancesPeople : AMBUSH_PLAN.distancesPeople) {
    const at = (people * person) / r;
    const backs = AMBUSH_PLAN.eyeBackPeople.map(b => ((people + b) * person) / r).filter(e => e < way);
    if (backs.length === 0) break;
    const here = slerp(point, src, at / way);
    // the eyes: on the way at each distance beyond, and off it to either side at the first (across the way, along the ground)
    const ons = backs.map(e => slerp(point, src, e / way));
    const eyes = [...ons, ...besides(ons[0], src, (AMBUSH_PLAN.eyeSidePeople * person) / r)].map(d => personPointOn(planet, vecNormalize(d), ps, ps.losEyeHeight));
    const pick = hiddenSpot(state, planetIndex, here, eyes, AMBUSH_PLAN);
    if (pick) return { dir: pick, people };
  }
  return null;
}

type Phase = 'moving' | 'waiting' | 'sprung' | 'holding';
/** One ambush: the squad, its point, when its site was picked and when it ended, its last phase and why it ended. */
export interface AmbushRecord { squadId: number; pointId: number; picked: number; ended: number; last: Phase; why: 'order' | 'claimed' | 'few' | 'noSite' | 'clear' | 'holdMax' | 'gone' | 'early'; by?: string }
interface Lie { pointId: number; planetIndex: number; site: Vec3; phase: Phase; since: number; picked: number; issued: number; clearFrom?: number; holdFrom?: number }
interface PlanSquad { id: number; unitIds: number[] }
/** The commander's issue as this planner uses it (ai/squadOrders.ts IssueOrder, the claim with its skill). */
export type AmbushIssue<Sq extends PlanSquad> = (squad: Sq, order: SquadOrder, tier: 'base' | 'task', expires?: number, units?: number[], claim?: { claim?: TaskClaim; skill?: SkillTag }) => unknown;

/** The planner: one a side, its memory the squads lying in wait (and the ones cooling down). */
export function createAmbushPlanner(team: number) {
  const lying = new Map<number, Lie>();
  const cooling = new Map<number, number>();   // squad id → the tick it may lie in wait again
  const second = Math.max(1, Math.round(1 / PHYS_DT));

  const onFoot = (st: GameState, sq: PlanSquad) => sq.unitIds.map(id => st.players.find(p => p.id === id)).filter(u => !!u && u.alive && !u.mount && !u.airborne);
  const contactAt = (st: GameState, planetIndex: number, at: Vec3, people: number, tick: number) => {
    const r = st.planets[planetIndex].radius, person = PERSON_BODY_HEIGHT * personSetOf(st).k;
    return teamPicture(st, team, tick).some(b => b.planetIndex === planetIndex && arc(b.surfaceDir, at) * r <= people * person);
  };
  const give = <Sq extends PlanSquad>(issue: AmbushIssue<Sq>, sq: Sq, l: Lie, tick: number) => {
    l.issued = tick;
    return issue(sq, { kind: 'hold', planetIndex: l.planetIndex, surfaceDir: l.site, ambush: {} }, 'task', tick + AMBUSH_PLAN.taskSeconds * second, undefined,
      { claim: 'ambush', skill: { name: 'ambush', phase: l.phase, since: l.since } });
  };
  /** The ambushes' ledger: each squad's, from the pick to its end and why (the harness's and the tests' read). */
  const ledger: AmbushRecord[] = [];
  const end = (id: number, tick: number, why: AmbushRecord['why'], by?: string) => {
    const l = lying.get(id);
    if (l) ledger.push({ squadId: id, pointId: l.pointId, picked: l.picked, ended: tick, last: l.phase, why, ...(by ? { by } : {}) });
    lying.delete(id); cooling.set(id, tick + AMBUSH_PLAN.cooldownSeconds * second);
  };

  return {
    /** Which squads lie in wait now (squad id → its phase), for the harness and the tests. */
    phases: (): Map<number, Phase> => new Map([...lying].map(([id, l]) => [id, l.phase])),
    records: (): readonly AmbushRecord[] => ledger,
    /** Is squad `id` lying in wait (any phase)? The commander keeps its hold on the point meanwhile — it walked off the point to guard it, and the re-plan's nearest squad would otherwise take the hold over. */
    holds: (id: number): boolean => lying.has(id),

    /**
     * At the re-plan, after `plays` (the planner list): each squad of the side the plan holds on a point it owns, free for the
     * routine (`free`: nothing claims it above rank 6), `pickOnFoot` on foot (two to keep one), with no enemy known at the point and not cooling
     * down — a site read for it, and the task given. A squad already lying in wait whose order changed is let go (its task
     * lapses); one waiting past `repickSeconds` has its site read again.
     */
    plan<Sq extends PlanSquad>(st: GameState, squads: readonly Sq[], plan: ReadonlyMap<number, SquadOrder>, tick: number, free: (sq: Sq) => boolean, issue: AmbushIssue<Sq>): void {
      const sites = st.modeState.points?.sites ?? [];
      for (const sq of squads) {
        const o = plan.get(sq.id);
        const site = o?.kind === 'hold' && o.pointId !== undefined ? sites.find(s => s.id === o.pointId) : undefined;
        const l = lying.get(sq.id);
        if (!site || site.owner !== team || !free(sq) || onFoot(st, sq).length < (lying.has(sq.id) ? 2 : AMBUSH_PLAN.pickOnFoot)) { if (l) { const why = !site || site.owner !== team ? 'order' : !free(sq) ? 'claimed' : 'few'; end(sq.id, tick, why, why === 'claimed' ? takenBy(st, sq.unitIds, tick) : undefined); } continue; }
        if (l && l.pointId !== site.id) end(sq.id, tick, 'order');
        const now = lying.get(sq.id);
        if (now && !(now.phase === 'waiting' && tick - now.picked >= AMBUSH_PLAN.repickSeconds * second)) continue;
        if (!now && (cooling.get(sq.id) ?? -1) > tick) continue;
        if (!now && contactAt(st, site.planetIndex, site.surfaceDir, AMBUSH_PLAN.contactPeople, tick)) continue;
        const at = ambushSite(st, team, site.planetIndex, site.surfaceDir, tick);
        if (!at) { if (now) end(sq.id, tick, 'noSite'); continue; }
        if (!now && contactAt(st, site.planetIndex, at.dir, AMBUSH_PLAN.contactPeople, tick)) continue;
        if (now && arc(now.site, at.dir) * st.planets[site.planetIndex].radius <= AMBUSH_PLAN.arrivePeople * PERSON_BODY_HEIGHT * personSetOf(st).k) { now.picked = tick; continue; }   // (the same place: stays down)
        const next: Lie = { pointId: site.id, planetIndex: site.planetIndex, site: at.dir, phase: 'moving', since: tick, picked: tick, issued: tick };
        lying.set(sq.id, next);
        give(issue, sq, next, tick);
      }
    },

    /**
     * Once a second: each squad lying in wait — its phase from the ground and its leader's step (66.11's `ambush.sprung`),
     * the task given again when the phase moves or it is due for renewal; and let go once the fight at the site is over (no
     * enemy known within `clearZones` zones for `clearSeconds`, or `holdMaxSeconds` holding), when its people are down to
     * one, or when something else took the squad (`free` false).
     */
    step<Sq extends PlanSquad>(st: GameState, squads: readonly Sq[], tick: number, free: (sq: Sq) => boolean, issue: AmbushIssue<Sq>): void {
      if (tick % second !== 0) return;
      const person = PERSON_BODY_HEIGHT * personSetOf(st).k;
      for (const [id, l] of [...lying]) {
        const sq = squads.find(s => s.id === id);
        const us = sq ? onFoot(st, sq) : [];
        if (!sq || us.length < 2 || !free(sq)) { end(id, tick, !sq ? 'gone' : us.length < 2 ? 'few' : 'claimed', sq && us.length >= 2 ? takenBy(st, sq.unitIds, tick) : undefined); continue; }
        const r = st.planets[l.planetIndex].radius;
        // the leader's step at this site (66.11: armed when the leader came inside the hold's zone, sprung by its brain)
        const lead = sq.unitIds.map(u => brainFace(st, u)?.lead).find(x => x?.kind === 'hold' && x.ambush && x.planetIndex === l.planetIndex && arc(x.to, l.site) * r <= AMBUSH_PLAN.arrivePeople * person);
        let phase: Phase = l.phase;
        if (lead?.ambush?.sprung !== undefined && (phase === 'moving' || phase === 'waiting')) phase = 'sprung';
        else if (phase === 'moving' && lead) phase = 'waiting';
        else if (phase === 'sprung' && tick - l.since >= AMBUSH_PLAN.sprungSeconds * second) phase = 'holding';
        if (phase === 'holding') {
          l.holdFrom ??= tick;
          const near = contactAt(st, l.planetIndex, l.site, AMBUSH_PLAN.clearZones * AMBUSH.zonePeople, tick);
          if (near) l.clearFrom = undefined; else l.clearFrom ??= tick;
          if (l.clearFrom !== undefined && tick - l.clearFrom >= AMBUSH_PLAN.clearSeconds * second) { end(id, tick, 'clear'); continue; }
          if (tick - l.holdFrom >= AMBUSH_PLAN.holdMaxSeconds * second) { end(id, tick, 'holdMax'); continue; }
        }
        // sprung with no enemy the side knows of near the site (found first — hit, or fired at from afar): the surprise is
        // gone, so back to the point rather than fight from the forward spot (Foothold's tug, fh-1/3: 7 of 7 springs came so)
        if (phase === 'sprung' && l.phase !== 'sprung' && !contactAt(st, l.planetIndex, l.site, AMBUSH_PLAN.clearZones * AMBUSH.zonePeople, tick)) { end(id, tick, 'early'); continue; }
        if (phase !== l.phase) { l.phase = phase; l.since = tick; give(issue, sq, l, tick); }
        else if (tick - l.issued >= AMBUSH_PLAN.renewSeconds * second) give(issue, sq, l, tick);
      }
    },
  };
}
